import { X as XIcon } from 'phosphor-react';
import { useEffect, useMemo, useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import {
  updateSupplierReminderSettings,
  updateSupplier,
} from '../../../../../../redux/services/suppliersService';
import { openDillToast } from '../../../../../../utils/helpers';
import { Supplier } from '@dill/dill-shared';
import { getUserDetails } from '../../../../../../redux/services/authService';
import { openMessageModal } from '../../../../../../redux/globalSlices/genericSlice';
import { DillButton, DillField, DillInput, DillToggle } from '../../../../../general/Dill';

interface PaymentReminderSettings {
  isSendEmailReminders: boolean;
  isAdminEmailSender: boolean;
  includeAttachments: boolean;
  includeAttachmentsToSpecificBuyers: boolean;
  mergeAttachments: boolean;
  isManualReminderDeactivated: boolean;
  numberOfDaysFromCurrentDateToDueDate: number[];
  numberOfDaysFromCurrentDateToCreditHoldDate: number[];
}

interface InvoicePaymentReminderSettingsSectionProps {
  supplier?: Supplier | null;
}

const getInitialSettings = (supplier?: Supplier | null): PaymentReminderSettings => ({
  isSendEmailReminders: supplier?.invoicePaymentReminderSettings?.isSendEmailReminders ?? false,
  isAdminEmailSender: supplier?.invoicePaymentReminderSettings?.isAdminEmailSender ?? false,
  includeAttachments: supplier?.invoicePaymentReminderSettings?.includeAttachments ?? false,
  includeAttachmentsToSpecificBuyers:
    supplier?.invoicePaymentReminderSettings?.includeAttachmentsToSpecificBuyers ?? false,
  mergeAttachments: supplier?.invoicePaymentReminderSettings?.mergeAttachments ?? false,
  isManualReminderDeactivated:
    supplier?.invoicePaymentReminderSettings?.isManualReminderDeactivated ?? false,
  numberOfDaysFromCurrentDateToDueDate:
    supplier?.invoicePaymentReminderSettings?.numberOfDaysFromCurrentDateToDueDate ?? [],
  numberOfDaysFromCurrentDateToCreditHoldDate:
    supplier?.invoicePaymentReminderSettings?.numberOfDaysFromCurrentDateToCreditHoldDate ?? [],
});

const TOGGLE_ROWS: {
  key: keyof Pick<
    PaymentReminderSettings,
    | 'isManualReminderDeactivated'
    | 'includeAttachments'
    | 'mergeAttachments'
    | 'isSendEmailReminders'
  >;
  label: string;
  description: string;
  inverted?: boolean;
  // Only rendered when this setting is also enabled
  dependsOn?: keyof PaymentReminderSettings;
}[] = [
  {
    key: 'isManualReminderDeactivated',
    label: 'Manual Reminders',
    description: 'Allow users to manually send payment reminder emails to customers.',
    inverted: true,
  },
  {
    key: 'includeAttachments',
    label: 'Include Attachments',
    description: 'Attach invoice PDFs to outgoing payment reminder emails.',
  },
  {
    key: 'mergeAttachments',
    label: 'Combine Attachments Into One File',
    description:
      'Merge all attached invoice PDFs into a single combined file instead of separate attachments.',
    dependsOn: 'includeAttachments',
  },
  {
    key: 'isSendEmailReminders',
    label: 'Automatic Email Reminders',
    description: 'Send payment reminder emails automatically on a scheduled basis.',
  },
];

export const InvoicePaymentReminderSettingsSection = ({
  supplier,
}: InvoicePaymentReminderSettingsSectionProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);

  const canEdit = useMemo(() => {
    if (!currentUser?.email || !supplier) return false;
    const email = currentUser.email.toLowerCase();
    const supplierEmail = supplier.email?.toLowerCase() ?? '';
    const adminEmails = (supplier.adminSupplierEmails ?? []).map((e) => e.toLowerCase());
    return (
      email === supplierEmail ||
      adminEmails.includes(email) ||
      (currentUser.roles?.includes('DILL_ADMIN') ?? false)
    );
  }, [currentUser, supplier]);

  const [settings, setSettings] = useState<PaymentReminderSettings>(() =>
    getInitialSettings(supplier)
  );
  const [initialSettings, setInitialSettings] = useState<PaymentReminderSettings>(() =>
    getInitialSettings(supplier)
  );
  const [saving, setSaving] = useState(false);

  const [dueDateDayInput, setDueDateDayInput] = useState('');
  const [dueDateDayError, setDueDateDayError] = useState('');
  const [creditHoldDateDayInput, setCreditHoldDateDayInput] = useState('');
  const [creditHoldDateDayError, setCreditHoldDateDayError] = useState('');

  const [discountReminderEnabled, setDiscountReminderEnabled] = useState(
    supplier?.discountReminderSettings?.isDiscountReminderEnabled ?? false
  );
  const [initialDiscountReminderEnabled, setInitialDiscountReminderEnabled] = useState(
    supplier?.discountReminderSettings?.isDiscountReminderEnabled ?? false
  );
  const [discountReminderDays, setDiscountReminderDays] = useState<number[]>(
    [...(supplier?.discountReminderSettings?.numberOfDaysBeforeDiscountDate ?? [])].sort(
      (a, b) => a - b
    )
  );
  const [initialDiscountReminderDays, setInitialDiscountReminderDays] = useState<number[]>(
    [...(supplier?.discountReminderSettings?.numberOfDaysBeforeDiscountDate ?? [])].sort(
      (a, b) => a - b
    )
  );
  const [discountDayInput, setDiscountDayInput] = useState('');
  const [discountInputError, setDiscountInputError] = useState('');

  useEffect(() => {
    const newSettings = getInitialSettings(supplier);
    setSettings(newSettings);
    setInitialSettings(newSettings);

    const enabled = supplier?.discountReminderSettings?.isDiscountReminderEnabled ?? false;
    const days = [
      ...(supplier?.discountReminderSettings?.numberOfDaysBeforeDiscountDate ?? []),
    ].sort((a, b) => a - b);
    setDiscountReminderEnabled(enabled);
    setInitialDiscountReminderEnabled(enabled);
    setDiscountReminderDays(days);
    setInitialDiscountReminderDays(days);
  }, [supplier]);

  const discountReminderChanged = useMemo(
    () =>
      discountReminderEnabled !== initialDiscountReminderEnabled ||
      discountReminderDays.length !== initialDiscountReminderDays.length ||
      discountReminderDays.some((d, i) => d !== initialDiscountReminderDays[i]),
    [
      discountReminderEnabled,
      initialDiscountReminderEnabled,
      discountReminderDays,
      initialDiscountReminderDays,
    ]
  );

  const settingsChanged = useMemo(
    () =>
      settings.isSendEmailReminders !== initialSettings.isSendEmailReminders ||
      settings.isAdminEmailSender !== initialSettings.isAdminEmailSender ||
      settings.includeAttachments !== initialSettings.includeAttachments ||
      settings.includeAttachmentsToSpecificBuyers !==
        initialSettings.includeAttachmentsToSpecificBuyers ||
      settings.mergeAttachments !== initialSettings.mergeAttachments ||
      settings.isManualReminderDeactivated !== initialSettings.isManualReminderDeactivated ||
      JSON.stringify(settings.numberOfDaysFromCurrentDateToDueDate) !==
        JSON.stringify(initialSettings.numberOfDaysFromCurrentDateToDueDate) ||
      JSON.stringify(settings.numberOfDaysFromCurrentDateToCreditHoldDate) !==
        JSON.stringify(initialSettings.numberOfDaysFromCurrentDateToCreditHoldDate) ||
      discountReminderChanged,
    [settings, initialSettings, discountReminderChanged]
  );

  const addDueDateDay = () => {
    const trimmed = dueDateDayInput.trim();
    const day = parseInt(trimmed);
    if (!trimmed || isNaN(day)) {
      setDueDateDayError('Enter a valid number');
      return;
    }
    if (settings.numberOfDaysFromCurrentDateToDueDate?.includes(day)) {
      setDueDateDayError('This day is already added');
      return;
    }
    setSettings({
      ...settings,
      numberOfDaysFromCurrentDateToDueDate: [
        ...(settings.numberOfDaysFromCurrentDateToDueDate || []),
        day,
      ].sort((a, b) => a - b),
    });
    setDueDateDayInput('');
    setDueDateDayError('');
  };

  const removeDueDateDay = (dayToRemove: number) => {
    setSettings({
      ...settings,
      numberOfDaysFromCurrentDateToDueDate: settings.numberOfDaysFromCurrentDateToDueDate.filter(
        (day) => day !== dayToRemove
      ),
    });
  };

  const addCreditHoldDateDay = () => {
    const trimmed = creditHoldDateDayInput.trim();
    const day = parseInt(trimmed);
    if (!trimmed || isNaN(day)) {
      setCreditHoldDateDayError('Enter a valid number');
      return;
    }
    if (settings.numberOfDaysFromCurrentDateToCreditHoldDate?.includes(day)) {
      setCreditHoldDateDayError('This day is already added');
      return;
    }
    setSettings({
      ...settings,
      numberOfDaysFromCurrentDateToCreditHoldDate: [
        ...(settings.numberOfDaysFromCurrentDateToCreditHoldDate || []),
        day,
      ].sort((a, b) => a - b),
    });
    setCreditHoldDateDayInput('');
    setCreditHoldDateDayError('');
  };

  const removeCreditHoldDateDay = (dayToRemove: number) => {
    setSettings({
      ...settings,
      numberOfDaysFromCurrentDateToCreditHoldDate:
        settings.numberOfDaysFromCurrentDateToCreditHoldDate.filter((day) => day !== dayToRemove),
    });
  };

  const handleAddDiscountDay = () => {
    const parsed = parseInt(discountDayInput, 10);
    if (isNaN(parsed) || parsed < 0) {
      setDiscountInputError('Enter a non-negative number');
      return;
    }
    if (discountReminderDays.includes(parsed)) {
      setDiscountInputError('Day already added');
      return;
    }
    setDiscountReminderDays((prev) => [...prev, parsed].sort((a, b) => a - b));
    setDiscountDayInput('');
    setDiscountInputError('');
  };

  const getChangedSettings = (): Partial<PaymentReminderSettings> => {
    const changed: Partial<PaymentReminderSettings> = {};
    if (settings.isSendEmailReminders !== initialSettings.isSendEmailReminders)
      changed.isSendEmailReminders = settings.isSendEmailReminders;
    if (settings.isAdminEmailSender !== initialSettings.isAdminEmailSender)
      changed.isAdminEmailSender = settings.isAdminEmailSender;
    if (settings.includeAttachments !== initialSettings.includeAttachments)
      changed.includeAttachments = settings.includeAttachments;
    if (
      settings.includeAttachmentsToSpecificBuyers !==
      initialSettings.includeAttachmentsToSpecificBuyers
    )
      changed.includeAttachmentsToSpecificBuyers = settings.includeAttachmentsToSpecificBuyers;
    if (settings.mergeAttachments !== initialSettings.mergeAttachments)
      changed.mergeAttachments = settings.mergeAttachments;
    if (settings.isManualReminderDeactivated !== initialSettings.isManualReminderDeactivated)
      changed.isManualReminderDeactivated = settings.isManualReminderDeactivated;
    if (
      JSON.stringify(settings.numberOfDaysFromCurrentDateToDueDate) !==
      JSON.stringify(initialSettings.numberOfDaysFromCurrentDateToDueDate)
    )
      changed.numberOfDaysFromCurrentDateToDueDate = settings.numberOfDaysFromCurrentDateToDueDate;
    if (
      JSON.stringify(settings.numberOfDaysFromCurrentDateToCreditHoldDate) !==
      JSON.stringify(initialSettings.numberOfDaysFromCurrentDateToCreditHoldDate)
    )
      changed.numberOfDaysFromCurrentDateToCreditHoldDate =
        settings.numberOfDaysFromCurrentDateToCreditHoldDate;
    return changed;
  };

  const shouldSkipWarningModal = (): boolean => {
    const changedKeys = Object.keys(getChangedSettings());
    const skipWarningKeys = ['isManualReminderDeactivated', 'isSendEmailReminders'];
    return changedKeys.every((key) => skipWarningKeys.includes(key));
  };

  const performSave = async (changedSettings: Partial<PaymentReminderSettings>) => {
    setSaving(true);
    const promises: Promise<any>[] = [];

    if (Object.keys(changedSettings).length > 0) {
      promises.push(
        dispatch(
          updateSupplierReminderSettings({
            supplierId: supplier?.id || '',
            invoicePaymentReminderSettings: changedSettings,
          })
        )
      );
    }

    if (discountReminderChanged) {
      promises.push(
        dispatch(
          updateSupplier({
            supplierId: supplier?.id || '',
            supplierDetails: {
              discountReminderSettings: {
                isDiscountReminderEnabled: discountReminderEnabled,
                numberOfDaysBeforeDiscountDate: discountReminderDays,
              },
            },
          })
        )
      );
    }

    const results = await Promise.all(promises);
    setSaving(false);

    const allSucceeded = results.every((r) => r.type?.endsWith('/fulfilled'));
    if (allSucceeded) {
      await dispatch(getUserDetails());
      openDillToast({ type: 'SUCCESS', message: 'Reminder settings saved' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to save reminder settings' });
    }
  };

  const handleSave = async () => {
    const changedSettings = getChangedSettings();
    if (shouldSkipWarningModal()) {
      await performSave(changedSettings);
      return;
    }
    dispatch(
      openMessageModal({
        modalType: 'WARN',
        title: 'Update Reminder Settings?',
        subTitle:
          'Saving these changes will update the reminder settings for all customers. Any individual customizations made to specific customers will be overwritten.',
        buttonText: 'Cancel',
        secondButtonText: 'Proceed',
        isSecondButton: true,
        onSecondButtonClick: async () => {
          await performSave(changedSettings);
        },
      })
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900">Invoice Payment Reminders</h4>
        <p className="text-xs text-gray-400 mt-0.5">
          Configure how payment reminder emails are sent to customers.
        </p>
      </div>

      <div className="flex flex-col gap-1 flex-1">
        {TOGGLE_ROWS.filter((row) => !row.dependsOn || settings[row.dependsOn]).map(
          (row, i, visibleRows) => (
            <label
              key={row.key}
              className={`flex items-center justify-between gap-4 py-3 cursor-pointer ${
                i < visibleRows.length - 1 ? 'border-b border-gray-100' : ''
              } ${row.dependsOn ? 'pl-4' : ''}`}>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-800">{row.label}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{row.description}</p>
              </div>
              <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
                <DillToggle
                  value={row.inverted ? !settings[row.key] : !!settings[row.key]}
                  onChange={() => setSettings((prev) => ({ ...prev, [row.key]: !prev[row.key] }))}
                  isDisabled={!canEdit}
                />
              </div>
            </label>
          )
        )}

        {/* Reminder day schedule — only visible when auto reminders are on */}
        {settings.isSendEmailReminders && (
          <div
            className="mt-2 border border-gray-200 rounded-lg p-3 flex flex-col gap-4"
            data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
            <p className="text-xs font-semibold text-gray-700 m-0">Reminder Schedule</p>

            {/* Due date days */}
            <div>
              <p className="text-[11px] text-gray-400 mb-2">
                Days relative to due date{' '}
                <span className="text-gray-300">(negative = past due)</span>
              </p>
              <div className="flex items-start gap-2 mb-2">
                <DillField label="" error={dueDateDayError} className="flex-1">
                  <DillInput
                    value={dueDateDayInput}
                    onChange={(v) => {
                      setDueDateDayInput(v);
                      setDueDateDayError('');
                    }}
                    placeholder="e.g. 7"
                    type="number"
                    error={dueDateDayError}
                    disabled={!canEdit}
                  />
                </DillField>
                <DillButton
                  label="Add"
                  variant="secondary"
                  onClick={addDueDateDay}
                  isDisabled={!canEdit || !dueDateDayInput.trim()}
                />
              </div>
              {settings.numberOfDaysFromCurrentDateToDueDate.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {settings.numberOfDaysFromCurrentDateToDueDate.map((day) => (
                    <span
                      key={day}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 border border-blue-200 text-[11px] font-medium text-blue-700">
                      {day > 0
                        ? `${day} day${day === 1 ? '' : 's'} before due`
                        : `${Math.abs(day)} day${Math.abs(day) === 1 ? '' : 's'} after due`}
                      <button
                        onClick={() => removeDueDateDay(day)}
                        disabled={!canEdit}
                        className="text-blue-400 hover:text-blue-700 cursor-pointer bg-transparent border-none leading-none disabled:opacity-50 disabled:cursor-not-allowed">
                        <XIcon size={10} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Credit hold date days */}
            <div>
              <p className="text-[11px] text-gray-400 mb-2">
                Days past due to send &ldquo;nearing credit hold&rdquo; reminder{' '}
                <span className="text-gray-300">(negative = past due)</span>
              </p>
              <div className="flex items-start gap-2 mb-2">
                <DillField label="" error={creditHoldDateDayError} className="flex-1">
                  <DillInput
                    value={creditHoldDateDayInput}
                    onChange={(v) => {
                      setCreditHoldDateDayInput(v);
                      setCreditHoldDateDayError('');
                    }}
                    placeholder="e.g. 3"
                    type="number"
                    error={creditHoldDateDayError}
                    disabled={!canEdit}
                  />
                </DillField>
                <DillButton
                  label="Add"
                  variant="secondary"
                  onClick={addCreditHoldDateDay}
                  isDisabled={!canEdit || !creditHoldDateDayInput.trim()}
                />
              </div>
              {settings.numberOfDaysFromCurrentDateToCreditHoldDate.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {settings.numberOfDaysFromCurrentDateToCreditHoldDate.map((day) => (
                    <span
                      key={day}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 border border-blue-200 text-[11px] font-medium text-blue-700">
                      {day > 0
                        ? `${day} day${day === 1 ? '' : 's'} before due`
                        : `${Math.abs(day)} day${Math.abs(day) === 1 ? '' : 's'} after due`}
                      <button
                        onClick={() => removeCreditHoldDateDay(day)}
                        disabled={!canEdit}
                        className="text-blue-400 hover:text-blue-700 cursor-pointer bg-transparent border-none leading-none disabled:opacity-50 disabled:cursor-not-allowed">
                        <XIcon size={10} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-gray-100 pt-4 mt-2">
        <h5 className="text-xs font-semibold text-gray-900 mb-1">Discount Reminders</h5>
        <p className="text-[11px] text-gray-400 mb-3">
          Send customers a reminder email before their early payment discount expires.
        </p>

        <label className="flex items-center justify-between gap-4 py-2 cursor-pointer">
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-800">Enable Discount Reminders</p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Automatically notify customers before their discount deadline.
            </p>
          </div>
          <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
            <DillToggle
              value={discountReminderEnabled}
              onChange={() => setDiscountReminderEnabled((prev) => !prev)}
              isDisabled={!canEdit}
            />
          </div>
        </label>

        {discountReminderEnabled && (
          <div className="mt-3">
            <p className="text-[11px] text-gray-500 mb-2">
              Days before discount date to send reminder:
            </p>
            <div className="flex gap-2">
              <input
                type="number"
                min={0}
                value={discountDayInput}
                onChange={(e) => {
                  setDiscountDayInput(e.target.value);
                  setDiscountInputError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleAddDiscountDay()}
                placeholder="e.g. 3"
                className="border border-gray-200 rounded-md px-2 py-1.5 text-xs w-24 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <button
                onClick={handleAddDiscountDay}
                disabled={!canEdit}
                className="px-3 py-1.5 text-xs font-medium rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-40">
                Add
              </button>
            </div>
            {discountInputError && (
              <p className="text-[11px] text-red-500 mt-1">{discountInputError}</p>
            )}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {discountReminderDays.length > 0 ? (
                discountReminderDays.map((day) => (
                  <span
                    key={day}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 border border-blue-200 text-[11px] font-medium text-blue-700">
                    {day === 0 ? 'Same day' : `${day} day${day !== 1 ? 's' : ''}`}
                    {canEdit && (
                      <button
                        onClick={() =>
                          setDiscountReminderDays((prev) => prev.filter((d) => d !== day))
                        }
                        className="text-blue-400 hover:text-blue-700 cursor-pointer bg-transparent border-none leading-none">
                        <XIcon size={10} />
                      </button>
                    )}
                  </span>
                ))
              ) : (
                <p className="text-[11px] text-gray-400 italic">No days configured yet.</p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
          <DillButton
            onClick={handleSave}
            label="Save changes"
            isLoading={saving}
            isDisabled={!canEdit || !settingsChanged}
          />
        </div>
      </div>

      {!canEdit && (
        <ReactTooltip
          id="no-permission-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          You do not have permission to make changes
        </ReactTooltip>
      )}
    </div>
  );
};
